<script setup lang="ts">
import type { Group, GroupMember } from '~/models/Group'
import type { ContentFormWriter } from '~/models/Content/ContentFormWriter'
import { useRoute }  from '#imports'
import { useI18n } from 'vue-i18n'
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { faFileLines } from '@fortawesome/duotone-regular-svg-icons'
import useGroupsStore from '~/stores/groups'
import { PendoFeature } from '~/models/Pendo'
import { GroupRole } from '~/models/Group'
import { useAppColor } from '~/composables/useAppColor'
import useAssignmentApi from '~/api/assignmentApi'
import Select from '~/components/ui/Select.vue'
import AuSpinner from '~/components/ui/AuSpinner.vue'
import AuCallout from '~/components/ui/AuCallout.vue'

const { item } = defineProps<{ item: ContentFormWriter }>()

const activeStudent = defineModel<GroupMember | null>()

const { t } = useI18n()
const { themeColor } = useAppColor()
const { loadGroupMembersById, sortMembers } = useGroupsStore()
const { sortedGroups, groupMembers, hasLoadedGroups } = storeToRefs(useGroupsStore())
const { getAssignmentResponse } = useAssignmentApi()
const route = useRoute()

const memberId = route.query?.memberId || ''
const groupId = route.query?.groupId || ''

const activeGroup = ref<Group | null>(null)

const assignmentId = computed(() => `form_writer-${item.locationId}`)
const activeGroupId = computed(() => activeGroup.value?.groupId ?? '')

const students = computed(() => {
  if (!activeGroup.value?.groupId || !groupMembers.value?.[activeGroup.value.groupId]?.length)
    return []

  return groupMembers.value[activeGroup.value.groupId]
    .filter(member => member.groupRole === GroupRole.Member)
    .sort(sortMembers)
})

const studentsWithAnswers = computed(() =>
  students.value?.filter(student => hasAnswer(student.userId)))

const studentsWithoutAnswers = computed(() =>
  students.value?.filter(student => !hasAnswer(student.userId)))

const hasAnswer = (userId: string) => {
  const response = data.value?.find(({ student }) => student.userId === userId)
  return !!response?.answer && response.answer.length > 0
}

const { data, isLoading } = useQuery({
  queryKey: computed(() => ['form_writer_responses', assignmentId.value, activeGroupId.value]),
  queryFn: () => getAssignmentResponse(assignmentId.value, [activeGroupId.value]),
  enabled: computed(() => !!assignmentId.value && !!activeGroupId.value),
  staleTime: Infinity
})

watch(activeGroup, group =>
  group?.groupId ? loadGroupMembersById(group.groupId) : (activeStudent.value = null))

const stopGroupWatcher = watch(sortedGroups, () => {
  if (groupId && sortedGroups.value.length) {
    activeGroup.value = sortedGroups.value.find(g => g.groupId === groupId) ?? null
    queueMicrotask(() => stopGroupWatcher())
  }
}, { immediate: true })

const stopStudentWatcher = watch(studentsWithAnswers, () => {
  if (memberId && studentsWithAnswers.value.length) {
    activeStudent.value = studentsWithAnswers.value.find(s => s.userId === memberId) ?? null
    queueMicrotask(() => stopStudentWatcher())
  }
})

const reset = () => {
  activeStudent.value = null
  activeGroup.value = null
}
</script>

<template>
  <div class="xs:flex-nowrap sticky top-0 z-10 mx-auto flex flex-wrap items-center gap-2 rounded-xl border-2 border-(--theme-40) bg-(--theme-10) p-4 pt-9 print:hidden">
    <div class="absolute top-0 left-0 flex items-center gap-1.5 rounded-tl-lg rounded-br-lg bg-(--theme-40) px-2 py-1 text-sm font-medium text-(--theme-5)">
      <font-awesome-icon :icon="faFileLines" />
      <label v-text="t('formWriter.delivered')" />
    </div>
    <AuCallout
      v-if="hasLoadedGroups && !sortedGroups.length"
      variant="warning"
      :title="t('formWriter.callouts.missingGroups')"
      class="my-2"
    />
    <label v-if="sortedGroups.length" class="flex flex-col gap-1">
      <span
        class="text-sm font-medium"
        v-text="t('formWriter.labels.group')"
      />
      <Select
        v-model="activeGroup"
        :options="sortedGroups"
        :color="themeColor"
        shade="light"
        size="lg"
        :placeholder="t('formWriter.labels.group')"
        :option-label="(group: Group) => group.name"
        :data-pendo="PendoFeature.FormWriter.SelectGroup"
      />
    </label>
    <AuSpinner
      v-if="isLoading"
      class="self-end"
      size="sm"
    />
    <label v-if="activeGroup && !isLoading" class="flex flex-col gap-1">
      <span
        class="text-sm font-medium"
        v-text="t('formWriter.labels.visited', { number: studentsWithAnswers.length })"
      />
      <Select
        v-model="activeStudent"
        :options="studentsWithAnswers"
        :placeholder="t('formWriter.labels.chooseStudent')"
        :option-label="(student: GroupMember) => student.fullName"
        :data-pendo="PendoFeature.FormWriter.SelectStudent"
        :no-options="t('formWriter.labels.noStudents')"
        :color="themeColor"
        size="lg"
        shade="light"
      />
    </label>
    <label v-if="activeGroup && !isLoading" class="flex flex-col gap-1">
      <span
        class="text-sm font-medium"
        v-text="t('formWriter.labels.notVisited', { number: studentsWithoutAnswers.length })"
      />
      <Select
        :options="studentsWithoutAnswers"
        :placeholder="t('formWriter.labels.showStudents')"
        :option-label="(member: GroupMember) => member.fullName"
        :no-options="t('formWriter.labels.noStudents')"
        :selectable="false"
        :color="themeColor"
        shade="light"
        size="lg"
      />
    </label>
    <button
      v-if="activeGroup"
      class="ml-auto self-end text-xs underline transition hover:bg-(--theme-20) focus-visible:ring"
      @click="reset"
    >
      {{ t('clear') }}
    </button>
  </div>
</template>
